import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ConstrainedView from "../../components/reactOL/views/ConstrainedView";
import { ViewContext } from "./ViewContext";
import * as olProj from "ol/proj";
import { createXYZ } from "ol/tilegrid";
import { useTenantContext } from "../Tenant/TenantContext";

interface ViewProps {
  children: React.ReactNode;
}

const ViewProvider = ({ children }: ViewProps) => {
  const [searchParams] = useSearchParams();
  const { config } = useTenantContext();
  const mapMaxZoom: number = config.mapMaxZoom ?? 15;

  const initialCenter = useMemo(() => {
    const param = searchParams.get("center");
    if (param) {
      const parts = param.split(",").map(Number);
      if (parts.length === 2 && !parts.some(isNaN)) return parts;
    }
    return [0, 0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialZoom = useMemo(() => {
    const param = searchParams.get("zoom");
    if (param) {
      const z = Number(param);
      if (!isNaN(z)) return z;
    }
    return 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const view = useMemo(
    () =>
      new ConstrainedView({
        center: initialCenter,
        zoom: initialZoom,
        projection: olProj.get("EPSG:3857")!,
        minZoom: 1,
        maxZoom: mapMaxZoom,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const projection = useMemo(() => olProj.get("EPSG:3857")!, []);

  const tileGrid = useMemo(
    () =>
      createXYZ({
        extent: projection.getExtent(),
        tileSize: 256,
        maxZoom: mapMaxZoom,
        minZoom: 0,
      }),
    [mapMaxZoom, projection],
  );

  const [loading, setLoading] = useState(false);

  return (
    <ViewContext.Provider
      value={{
        view,
        projection,
        tileGrid,
        loading,
        setLoading,
      }}
    >
      {children}
    </ViewContext.Provider>
  );
};

export default ViewProvider;
